import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Field from "../Field";
import PasswordInput from "../PasswordInput";
import { ShieldCheck } from "lucide-react";

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  t: any;
  editTab: "info" | "password";
  setEditTab: (tab: "info" | "password") => void;
  infoForm: { name: string; surname: string; phone: string };
  setInfoForm: React.Dispatch<React.SetStateAction<{ name: string; surname: string; phone: string }>>;
  infoErrors: Record<string, string>;
  pwForm: { current: string; password: string; confirm: string };
  setPwForm: React.Dispatch<React.SetStateAction<{ current: string; password: string; confirm: string }>>;
  pwErrors: Record<string, string>;
  loading: boolean;
  isVerified?: boolean;
  onSubmitInfo: () => void;
  onSubmitPassword: () => void;
}

const EditProfileDialog: React.FC<EditProfileDialogProps> = ({
  open,
  onOpenChange,
  t,
  editTab,
  setEditTab,
  infoForm,
  setInfoForm,
  infoErrors,
  pwForm,
  setPwForm,
  pwErrors,
  loading,
  isVerified,
  onSubmitInfo,
  onSubmitPassword,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("profile.title")}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl mb-4">
          {(["info", "password"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setEditTab(tab)}
              className={
                "flex-1 py-1.5 text-sm font-semibold rounded-lg transition-all " +
                (editTab === tab
                  ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200")
              }
            >
              {tab === "info" ? t("profile.basicInfo") : t("profile.password")}
            </button>
          ))}
        </div>

        {editTab === "info" ? (
          <div className="space-y-3">
            {isVerified && (
              <div className="flex items-center gap-2 rounded-xl bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 px-3 py-2 text-sm text-green-700 dark:text-green-300">
                <ShieldCheck className="w-4 h-4 shrink-0" />
                <span>Təsdiqlənmiş istifadəçilər ad və soyadını dəyişə bilməz.</span>
              </div>
            )}
            <Field label={t("auth.name")} error={infoErrors.name}>
              <Input
                value={infoForm.name}
                onChange={(e) => setInfoForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t("auth.name")}
                disabled={isVerified}
              />
            </Field>
            <Field label={t("auth.surname")} error={infoErrors.surname}>
              <Input
                value={infoForm.surname}
                onChange={(e) => setInfoForm((f) => ({ ...f, surname: e.target.value }))}
                placeholder={t("auth.surname")}
                disabled={isVerified}
              />
            </Field>
            <Field label="Əlaqə nömrəsi">
              <div className="flex">
                <span className="flex items-center px-3 border border-r-0 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 rounded-l-lg text-sm text-gray-600 dark:text-gray-300 font-medium">
                  +994
                </span>
                <Input
                  type="number"
                  value={infoForm.phone}
                  onChange={(e) => setInfoForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="XXXXXXXX"
                  className="rounded-l-none"
                />
              </div>
            </Field>
            <Button onClick={onSubmitInfo} disabled={loading} className="w-full mt-1">
              {t("profile.updateInfo")}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Field label={t("profile.currentPassword")} error={pwErrors.current}>
              <PasswordInput
                value={pwForm.current}
                onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))}
              />
            </Field>
            <Field label={t("profile.newPassword")} error={pwErrors.password}>
              <PasswordInput
                value={pwForm.password}
                onChange={(e) => setPwForm((f) => ({ ...f, password: e.target.value }))}
              />
            </Field>
            <Field label={t("profile.confirmNewPassword")} error={pwErrors.confirm}>
              <PasswordInput
                value={pwForm.confirm}
                onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
              />
            </Field>
            <Button onClick={onSubmitPassword} disabled={loading} className="w-full mt-1">
              {t("profile.updatePassword")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditProfileDialog;
